<template>
  <div
    class="ovp-configurator ovp-a11y-container"
    :app-language="selectedLanguage"
    :app-name="$props.source"
    :app-version="version$"
  >
    <template v-if="isReady">
      <system-notifications
        v-if="isApplicationReady"
        :language="selectedLanguage"
      />

      <router-view :route-meta="routeMeta" />

      <a-b-config />
      <div id="modals"></div>
      <medical-office-search />
      <the-confirmation />
      <the-appointment />
      <fab-container />
      <screen-auth />
    </template>

    <the-loading
      v-else
      text="Bitte warten | Please wait | S'il vous plaît, attendez | attendere prego"
      forced
    />

    <the-loading />
    <global-error-handler />
    <the-survey />
    <the-snackbar />
  </div>
</template>

<script setup>
  import { computed, inject, nextTick, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  import { registerBasketActionListeners, registerUpdateBasketListeners } from '@shared/helpers/GlobalEventHandler'
  import { logInfo } from '@shared/helpers/Logger'
  import { events$, loading$ } from '@shared/services'
  import { CookieUtil } from '@shared/utils/Cookie'

  import basketStore from '@shared/store/basket'
  import sessionStore from '@shared/store/session'
  import useApplication from '@shared/hooks/useApplication'
  import useI18n from '@shared/hooks/useI18n'
  import useSurvey from '@shared/hooks/useSurvey'
  import useSync from '@shared/hooks/useSync'
  import useUrl from '@shared/hooks/useUrl'

  import TheLoading from '@shared/components/Container/Loading'
  import GlobalErrorHandler from '@shared/components/Container/GlobalErrorHandler'
  import TheSurvey from '@shared/components/Survey/Survey'
  import TheSnackbar from '@shared/components/Basic/Snackbar'
  import SystemNotifications from '@shared/components/Container/SystemNotifications'
  import ABConfig from '@shared/components/Container/ABConfig'
  import FabContainer from '@shared/components/Fab/FabContainer'
  import MedicalOfficeSearch from '@shared/components/MedicalSearch/MedicalOfficeSearch'
  import ScreenAuth from '@shared/components/Screen/ScreenAuth'
  import TheAppointment from '@shared/components/Modal/Appointment'
  import TheConfirmation from '@shared/components/Modal/Confirmation'

  import { EVENT_DOTCOM_SURVEY_WEBSITE, EVENT_OVP, EVENT_URL, SOURCE } from '@shared/config/events'
  import { ROUTE_NAME, STORAGE_ENTRY_MESSAGES_KEY, SURVEY_TOUCHPOINT_WEBSITE } from '@shared/config/constants'

  import '@shared/assets/css/import.css'

  // HOOKS
  const { isContentReady, initializeClearSequence, isReadyWithoutPartner, isReadyWithPartner, preferedColorScheme } =
    useApplication()
  const { selectedLanguage } = useI18n()
  const route = useRoute()
  const router = useRouter()
  const { triggerSurvey } = useSurvey()
  const { unregisterSync } = useSync()
  const { getParamFromUrl, react } = useUrl()

  // INIT
  const version$ = inject('version$')
  defineProps({
    source: {
      type: String,
      default: SOURCE.CONFIGURATOR,
    },
  })

  // DATA
  let routeConfirmed = ref(false)

  // COMPUTED
  const isApplicationReady = computed(() => {
    if (routeMeta.value.isReentry) {
      return isReadyWithoutPartner.value && routeConfirmed.value
    } else {
      return isReadyWithPartner.value && routeConfirmed.value
    }
  })

  const isReady = computed(() => {
    // remove loading spinner on global error page
    if (route?.name === ROUTE_NAME.GLOBAL_ERROR) {
      return true
    }

    return isApplicationReady.value && isContentReady.value
  })

  const routeMeta = computed(() => route?.meta || {})

  // METHODS
  function emitEventWhenReady() {
    return new Promise(function (resolve) {
      if (isApplicationReady.value) resolve()

      const unwatch = watch(
        () => isApplicationReady.value,
        () => {
          unwatch()
          resolve()
        }
      )
    })
  }

  // WATCHERS
  watch(isReady, async value => {
    if (value) {
      logInfo(['%cLOCKED AND LOADED!', 'color: GREEN'])

      await nextTick()

      loading$.end()

      let infoMessages = CookieUtil.read(STORAGE_ENTRY_MESSAGES_KEY, false)
      if (infoMessages) {
        infoMessages = JSON.parse(infoMessages)

        infoMessages.forEach(message => {
          events$.emit(EVENT_OVP.ERROR, message)
        })

        CookieUtil.delete(STORAGE_ENTRY_MESSAGES_KEY, false)
      }

      const cmpIDCookie = CookieUtil.read('cmpID', false)
      if (!cmpIDCookie && !sessionStore.getSession()?.hot) {
        const cmpId = getParamFromUrl('cmpID')
        if (cmpId) {
          CookieUtil.set({
            key: 'cmpID',
            value: btoa(`${cmpId}:${Date.now()}`),
            withinEnv: false,
            lifetime: 604800,
          })
        }
      }
    }
  })

  // LIFECYCLE HOOKS
  onBeforeMount(() => {
    initializeClearSequence()

    loading$.start()

    events$.once(EVENT_DOTCOM_SURVEY_WEBSITE, () => {
      triggerSurvey(SURVEY_TOUCHPOINT_WEBSITE)
    })
  })

  onMounted(() => {
    document.querySelector(':root').style.setProperty('--color-scheme', preferedColorScheme())

    registerUpdateBasketListeners()
    registerBasketActionListeners(router)

    emitEventWhenReady().then(() => {
      // tab-sync needs to be registered, after all the BE-/localStorage operations are done
      // registerSync()

      // react to URL-params (e.g. open login)
      window.setTimeout(() => {
        react()
      }, 50)
    })

    events$.on(EVENT_URL.GOTO, opts => {
      if (!opts.type) opts.type = 'push'

      // new route can come as a string, or as a route-object
      if (opts.target) {
        if (basketStore.basket.persons.length > 0) {
          router[opts.type](opts.target.replace(':personId', basketStore.basket.persons[0].personId))
        }
      } else {
        router[opts.type](opts.route)
      }
    })

    document.addEventListener('keydown', event => {
      if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Tab'].includes(event.code)) {
        window.setTimeout(() => {
          if (document.activeElement?.classList && document.activeElement.classList.contains('outline')) {
            document.activeElement.classList.add('has-outline')
          }
        }, 10)
      }
    })
  })

  onBeforeUnmount(() => {
    unregisterSync()
  })

  router.isReady().then(() => {
    routeConfirmed.value = true
  })
</script>

<style name="global">
:root {
  /*
    New stacking contexts can be formed on an element in one of three ways:
    - When an element is the root element of a document (the <html> element)
    - When an element has a position value other than static and a z-index value other than auto
    - When an element has an opacity value less than 1
  */
  --dvp-stack-level-element: 1;
  --dvp-stack-level-footer: 1;
  --dvp-stack-level-header: 2;
  --dvp-stack-level-popup-menu: 2;
  --dvp-stack-level-modal: 3;
  --dvp-stack-level-modal-stacked: 4; /* exception for medical-office-search modal as it can be opened within another modal */
  --dvp-stack-level-popup-notification: 5;
  --dvp-stack-level-tooltip: 6;
  --dvp-stack-level-system-notification: 7;
  --dvp-stack-level-screen-blocker: 8;

  color-scheme: var(--color-scheme, 'light');
}

#chat-ui {
  position: relative;
  z-index: var(--dvp-stack-level-popup-menu);
}

*,
*:after,
*:before {
  box-sizing: border-box;
}

*:focus {
  outline: none;
}

html {
  overflow-y: scroll;
}

body {
  font-family: var(--font-family-open-sans);
  font-style: var(--font-style-default);
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-regular-lg);
  letter-spacing: var(--letter-spacing-regular-lg);
  color: var(--Text-Primary);
  background-color: var(--surface);

  margin: 0;
  /* FLEX-2954 prevent larger fonts for mobile landscape */
  -webkit-text-size-adjust: none;
}

p,
h1,
h2,
h3 {
  margin-block-start: 0;
  margin-block-end: 0;
}

a {
  color: inherit;
  text-decoration-line: none;
}

button,
input,
select,
textarea {
  font-family:
    Open Sans,
    sans-serif !important;
}

.tippy-box[data-theme~='dvp'] {
  min-width: 144px;
  padding: var(--fixed-spacing-fix-06);
  background-color: var(--surface);
  border-radius: var(--fixed-border-radius-fix-02);
  box-shadow: var(--elevation-level-5);

  .tippy-content {
    font-weight: var(--font-weight-regular);
    font-size: var(--font-size-regular-sm);
    letter-spacing: var(--letter-spacing-regular-sm);
    color: var(--on-surface);
    padding: 0;
  }

  .tippy-arrow {
    color: var(--on-primary);

    &::before {
      transform: scale(1.25);
    }
  }
}

.tippy-box[data-theme~='card'] {
  background-color: var(--surface);
  border-radius: var(--fixed-border-radius-fix-02);
  box-shadow: var(--elevation-level-5);
  padding: var(--fixed-spacing-fix-02);
  margin: 0;
  & > .tippy-content {
    padding: 0;
    margin: 0;
    height: max-content;

    & > .card__image {
      margin: 0;
      padding: 0;
      width: 100%;
    }
  }

  .tippy-arrow {
    color: var(--on-primary);

    &::before {
      transform: scale(1.25);
    }
  }
}

.tippy-box[data-placement='top-end'] > {
  .tippy-arrow {
    right: 20px !important;
    left: initial !important;
  }
}

.the-transition-enter-active,
.the-transition-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.the-transition-enter-from,
.the-transition-leave-to {
  opacity: 0;
}
</style>

<style scoped>
.ovp-configurator {
  display: grid;
  grid-template-areas:
    'alerts'
    'header'
    'content'
    'footer';
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto auto 1fr auto;
  height: 100vh;
}
</style>
